import React, { useEffect, useState } from "react";
import { Plus, X, Pencil, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateShort } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Panel, StatusBadge } from "../../lib/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

// item: { material_id, name, kind: "fabric"|"accessory" }
export function AdjustmentHistoryDialog({ item, setItem, warehouses, onDone }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => item && api.get("/production/material-adjustments", { params: { material_id: item.material_id } }).then((r) => setList(r.data));
  useEffect(() => { setEditing(null); if (item) load(); /* eslint-disable-next-line */ }, [item]);
  if (!item) return null;

  const cancel = async (a) => {
    if (!window.confirm(`Batalkan penyesuaian ${a.ref}? Stok akan dikembalikan seperti sebelum transaksi ini.`)) return;
    try { await api.post(`/production/material-adjustments/${a.id}/cancel`); toast.success("Dibatalkan, stok dikembalikan"); load(); onDone?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const describe = (a) => {
    if (a.type === "add_rolls") return (a.display || []).map((d) => `${d.lot || "-"}yd: +${num(d.qty)}`).join(", ");
    return (a.display || []).map((d) => `${d.bucket}${d.lot ? ` (lot ${d.lot})` : ""}: ${num(d.from)}→${num(d.to)}`).join(", ");
  };

  return (
    <Dialog open={!!item} onOpenChange={() => setItem(null)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-2"><History className="w-4 h-4" /> Riwayat Penyesuaian — {item.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-wmuted -mt-2 mb-1">Batalkan bila salah input; Edit untuk ubah angka. Hanya bisa jika material belum dipakai untuk cutting/alokasi.</p>
        <Panel>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Ref</TableHead><TableHead>Jenis</TableHead><TableHead>Perubahan</TableHead>
              <TableHead>Oleh</TableHead><TableHead>Waktu</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((a) => (
                <TableRow key={a.id} data-testid={`adj-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">{a.ref}</TableCell>
                  <TableCell className="text-xs">{a.type === "add_rolls" ? "Tambah Roll" : "Set Stok"}</TableCell>
                  <TableCell className="text-xs">{describe(a)}</TableCell>
                  <TableCell className="text-xs">{a.created_by}</TableCell>
                  <TableCell className="text-xs">{fmtDateShort(a.created_at)}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {a.status === "Aktif" && <>
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => setEditing(a)} data-testid={`adj-edit-${a.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>
                      <Button size="icon" variant="ghost" title="Batalkan" onClick={() => cancel(a)} data-testid={`adj-cancel-${a.id}`}><RotateCcw className="w-4 h-4 text-status-rejected" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
              {!list.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-8">Belum ada penyesuaian untuk material ini.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Panel>
        {editing && <EditAdjustmentForm adj={editing} warehouses={warehouses} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); onDone?.(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function EditAdjustmentForm({ adj, warehouses, onClose, onDone }) {
  const isRolls = adj.type === "add_rolls";
  const [wh, setWh] = useState(adj.warehouse_id || warehouses[0]?.id || "");
  const [reason, setReason] = useState(adj.reason || "Koreksi penyesuaian");
  const accBuckets = [["available", "Available"], ["production", "Production"], ["remaining", "Remaining"]];
  const fabBuckets = [["available", "Available"], ["cutting", "Cutting"], ["remaining", "Remaining"]];
  const setBuckets = adj.material_kind === "fabric" ? fabBuckets : accBuckets;
  const d0 = adj.display?.[0] || {};
  const [qty, setQty] = useState(d0.to ?? 0);
  const [bucket, setBucket] = useState(d0.bucket || "available");
  const [lot, setLot] = useState(d0.lot || "");
  const [rolls, setRolls] = useState(isRolls ? (adj.display || []).map((d) => ({ yard: d.lot || "", qty: (Number(d.lot) > 0 ? Math.round((Number(d.qty) / Number(d.lot)) * 100) / 100 : d.qty) || 0, bucket: d.bucket || "available" })) : [{ yard: "", qty: 0, bucket: "available" }]);
  const setRoll = (i, patch) => setRolls(rolls.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const save = async () => {
    if (!wh) return toast.error("Pilih gudang");
    try {
      const body = { warehouse_id: wh, reason };
      if (isRolls) {
        const rr = rolls.filter((r) => parseFloat(r.qty) > 0 && parseFloat(r.yard) > 0).map((r) => ({ yard: String(r.yard || ""), qty: (parseFloat(r.yard) || 0) * (parseFloat(r.qty) || 0), bucket: r.bucket }));
        if (!rr.length) return toast.error("Isi minimal satu roll");
        body.rolls = rr;
      } else {
        body.qty = parseFloat(qty) || 0; body.bucket = bucket; body.lot = lot;
      }
      await api.put(`/production/material-adjustments/${adj.id}`, body);
      toast.success("Penyesuaian diperbarui"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="border border-cocoa/30 rounded-lg p-4 mt-3 bg-ivory/40" data-testid="adj-edit-form">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-cocoa">Edit {adj.ref}</div>
        <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>
      <div className="grid gap-3">
        <div><Label>Gudang</Label>
          <Select value={wh} onValueChange={setWh}><SelectTrigger data-testid="adj-edit-wh"><SelectValue placeholder="Pilih gudang" /></SelectTrigger><SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
        </div>
        {isRolls ? (
          <div>
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[11px] text-wmuted px-1"><span>Yard / roll</span><span>Jml roll</span><span>Jenis</span><span></span></div>
            {rolls.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mt-1">
                <Input placeholder="mis. 50" value={r.yard} onChange={(e) => setRoll(i, { yard: e.target.value })} data-testid={`adj-roll-yard-${i}`} />
                <Input type="number" placeholder="jml" value={r.qty} onChange={(e) => setRoll(i, { qty: e.target.value })} data-testid={`adj-roll-qty-${i}`} />
                <Select value={r.bucket} onValueChange={(v) => setRoll(i, { bucket: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{fabBuckets.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
                <Button size="icon" variant="ghost" onClick={() => setRolls(rolls.filter((_, idx) => idx !== i))}><X className="w-4 h-4 text-status-rejected" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="border-taupe text-cocoa mt-2" onClick={() => setRolls([...rolls, { yard: "", qty: 0, bucket: "available" }])} data-testid="adj-add-roll"><Plus className="w-3.5 h-3.5 mr-1" />Tambah Roll</Button>
          </div>
        ) : (
          <>
            <div><Label>Lot</Label><Input value={lot} onChange={(e) => setLot(e.target.value)} data-testid="adj-edit-lot" /></div>
            <div><Label>Jenis Stok</Label>
              <Select value={bucket} onValueChange={setBucket}><SelectTrigger data-testid="adj-edit-bucket"><SelectValue /></SelectTrigger><SelectContent>{setBuckets.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Qty (set nilai)</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="adj-edit-qty" /></div>
          </>
        )}
        <div><Label>Alasan</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="adj-edit-save" className="bg-cocoa text-ivory">Simpan Perubahan</Button>
        </div>
      </div>
    </div>
  );
}
